import type { Patient, Reference } from '@medplum/fhirtypes'
import { codeableConcept } from '@seen/core/codeableConcept'
import { invariant } from '@seen/core/invariant'
import { ExtendedObservation } from '@seen/core/models/observation'
import { type JSX, useCallback, useRef } from 'react'
import { toast } from 'sonner'
import { FirstMatchDecorator } from '#src/components/rich-text-editor/ObservationNode/FirstMatchDecorator.ts'
import { ObservationNodeFrame } from '#src/components/rich-text-editor/ObservationNode/ObservationNodeFrame.tsx'
import {
  CHOOSE_PARTICIPANT,
  isForeignRecord,
} from '#src/components/rich-text-editor/ObservationNode/observationSubject.ts'
import { ObservationVitalInput } from '#src/components/rich-text-editor/ObservationNode/ObservationVitalInput.tsx'
import { ObservationVitalNote } from '#src/components/rich-text-editor/ObservationNode/ObservationVitalNote.tsx'
import type {
  ObservationCaptureTarget,
  ObservationNodeAttributes,
} from '#src/components/rich-text-editor/ObservationNode/types.ts'
import { useNodeRecord } from '#src/components/rich-text-editor/ObservationNode/useNodeRecord.ts'
import { withClinicianNote } from '#src/components/rich-text-editor/ObservationNode/vitalNote.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useDebouncedCallback } from '#src/hooks/useDebouncedCallback.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'

export function ObservationNodeNumber({
  attributes,
  updateAttributes,
  participant,
  noParticipant = false,
  editable = true,
  recordStatus,
}: {
  attributes: ObservationNodeAttributes
  updateAttributes: (attributes: Partial<ObservationNodeAttributes>) => void
  participant: Reference<Patient> | undefined
  // No participant chosen: nothing can be recorded.
  noParticipant?: boolean
  // Called by the node's record once a first answer is on the node.
  onReferencePersisted?: () => void
  // Whether the note can be changed, and so the vital cleared.
  editable?: boolean
  recordStatus?: ObservationCaptureTarget['recordStatus']
}): JSX.Element {
  const isReadOnly = useIsReadOnly()
  const record = useNodeRecord()
  const { observation } = record
  // Recorded for someone else than the editor's participant: shown, and
  // clearable, but not changed from here.
  const foreign = isForeignRecord(
    observation,
    noParticipant ? null : participant
  )
  // A note that cannot be changed (signed, posted, read-only) takes no
  // answer here, as every observation control.
  const locked = !editable || isReadOnly || noParticipant || foreign

  // A note written before the vital is recorded, for its record.
  const pendingNote = useRef('')
  const createObservationPromiseRef =
    useRef<Promise<ExtendedObservation> | null>(null)

  const practitioner = useCurrentPractitioner()

  const updateObservation = useCallback(
    async (observation: ExtendedObservation, html: string) => {
      const value = extractNumberFromHtml(html)
      try {
        await record.patch((o) => {
          o.updateValueQuantityValue(value ?? undefined)
          o.setNoteFirst(html, practitioner.reference)
        }, observation)
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? `Failed to update ${attributes.codingDisplay.toLowerCase()}: ${error.message}`
            : `Failed to update ${attributes.codingDisplay.toLowerCase()}`
        toast.error(errorMessage)
        throw error
      }
    },
    [record, practitioner.reference, attributes.codingDisplay]
  )

  const debouncedOnChange = useDebouncedCallback(async (html) => {
    if (!attributes.reference && !attributes.draft) {
      if (createObservationPromiseRef.current) {
        // Wait for the existing createObservationPromiseRef to finish before proceeding
        const createdObservation = await createObservationPromiseRef.current
        await updateObservation(createdObservation, html)
      } else {
        invariant(attributes.unit, 'unit is required')
        const value = extractNumberFromHtml(html)
        if (value === undefined) {
          toast.error(
            `Enter a number for ${attributes.codingDisplay.toLowerCase()}.`
          )
          return
        }
        createObservationPromiseRef.current = record.create(
          withClinicianNote(
            pendingNote.current,
            practitioner.reference,
            ExtendedObservation.fromValueQuantity({
              value,
              unit: attributes.unit,
              code: codeableConcept({
                code: attributes.code,
                system: attributes.system,
                display: attributes.codingDisplay,
              }),
              participant,
              practitioner: practitioner.reference,
              note: html,
              status: recordStatus,
            })
          )
        )
        try {
          await createObservationPromiseRef.current
        } catch (error) {
          const errorMessage =
            error instanceof Error
              ? `Failed to submit ${attributes.codingDisplay.toLowerCase()}: ${error.message}`
              : `Failed to submit ${attributes.codingDisplay.toLowerCase()}`
          toast.error(errorMessage)
          throw error
        } finally {
          createObservationPromiseRef.current = null
        }
      }
    } else {
      invariant(observation, 'observation is required')
      await updateObservation(observation, html)
    }
  }, 500) // Reduced from 2500ms to 500ms to mitigate race condition where users submit timeline notes before observation reference is created. Still debounced to avoid excessive version history entries, and we flush on blur as well.

  const handleAutofocused = useCallback(() => {
    updateAttributes({ autofocus: false })
  }, [updateAttributes])

  return (
    <ObservationNodeFrame
      attributes={attributes}
      label={ExtendedObservation.getNickname(
        attributes.code,
        attributes.codingDisplay
      )}
      className="relative"
      noHover
      // The line itself is the value's editor; a second one in the details
      // would write a value this line does not show.
      valueInDetails={false}
      participant={participant}
      foreign={foreign}
      blockedReason={
        noParticipant && !observation ? CHOOSE_PARTICIPANT : undefined
      }
      updateAttributes={editable ? updateAttributes : undefined}
    >
      <ObservationVitalInput
        code={attributes.code}
        value={
          observation?.noteFirstText ??
          observation?.valueQuantity?.toString() ??
          ''
        }
        debouncedOnChange={debouncedOnChange}
        additionalExtensions={additionalExtensions}
        autofocus={attributes.autofocus}
        onAutofocused={handleAutofocused}
        disabled={locked}
        noValue={!editable && !attributes.reference}
      />
      <ObservationVitalNote
        observation={observation}
        onPendingChange={(note) => {
          pendingNote.current = note
        }}
        disabled={locked}
      />
    </ObservationNodeFrame>
  )
}

const NUMBER_REGEX = /-?\d+(?:\.\d+)?/

const additionalExtensions = [
  FirstMatchDecorator.configure({
    regex: NUMBER_REGEX,
    className: 'font-mono tabular-nums text-foreground',
  }),
]

function extractNumberFromHtml(html: string): number | undefined {
  // Strip HTML tags and normalize whitespace
  const text = html
    .replace(/<[^>]+>/g, ' ') // remove tags
    .replace(/\s+/g, ' ') // collapse whitespace
    .trim()

  const match = text.match(NUMBER_REGEX)

  if (!match) {
    return undefined
  }

  return Number(match[0])
}
